import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BookOpen, Video, Loader2, Calendar, ExternalLink, FileText, Paperclip, Image as ImageIcon, Headphones, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import LessonPlayer from "./LessonPlayer";
import LessonSlidesViewer from "@/components/lesson/LessonSlidesViewer";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { ar } from "date-fns/locale";

interface Props {
  classId: string | null;
  nurseryId: string;
}

export default function ParentSchedule({ classId, nurseryId }: Props) {
  const [lessons, setLessons] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLesson, setSelectedLesson] = useState<any>(null);
  const [lessonFiles, setLessonFiles] = useState<any[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  const [showSlides, setShowSlides] = useState(false);

  // Fetch explanation for selected lesson
  const { data: lessonExplanation } = useQuery({
    queryKey: ["lesson-explanation-parent", selectedLesson?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("lesson_explanations")
        .select("*")
        .eq("lesson_id", selectedLesson!.id)
        .eq("status", "ready")
        .maybeSingle();
      return data;
    },
    enabled: !!selectedLesson?.id,
  });

  const hasSlides = lessonExplanation && (lessonExplanation.slides as any[])?.length > 0;

  useEffect(() => {
    fetchSchedule();
  }, [classId, nurseryId]);

  const fetchSchedule = async () => {
    setLoading(true);
    const now = new Date().toISOString();

    if (classId) {
      const [lessonsRes, sessionsRes] = await Promise.all([
        supabase
          .from("lessons")
          .select("*")
          .eq("nursery_id", nurseryId)
          .eq("class_id", classId)
          .order("scheduled_at", { ascending: true })
          .limit(50),
        supabase
          .from("live_sessions")
          .select("*")
          .eq("nursery_id", nurseryId)
          .eq("class_id", classId)
          .gte("scheduled_at", now)
          .order("scheduled_at", { ascending: true })
          .limit(10),
      ]);
      setLessons(lessonsRes.data || []);
      setSessions(sessionsRes.data || []);
    }

    setLoading(false);
  };

  const openLesson = async (lesson: any) => {
    setSelectedLesson(lesson);
    setLoadingFiles(true);
    try {
      const { data } = await supabase
        .from("lesson_files")
        .select("*")
        .eq("lesson_id", lesson.id)
        .eq("nursery_id", nurseryId);
      
      if (data && data.length > 0) {
        // Get signed URLs for files
        const filesWithUrls = await Promise.all(
          data.map(async (f: any) => {
            const bucketAndPath = f.file_url?.replace(/^.*\/storage\/v1\/object\/(?:public|sign)\//, "");
            if (!bucketAndPath) return { ...f, signed_url: f.file_url };
            const slashIndex = bucketAndPath.indexOf("/");
            const bucket = bucketAndPath.substring(0, slashIndex);
            const path = bucketAndPath.substring(slashIndex + 1);
            const { data: signedData } = await supabase.storage
              .from(bucket)
              .createSignedUrl(path, 3600);
            return { ...f, signed_url: signedData?.signedUrl || f.file_url };
          })
        );
        setLessonFiles(filesWithUrls);
      } else {
        setLessonFiles([]);
      }
    } catch {
      setLessonFiles([]);
    }
    setLoadingFiles(false);
  };

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardContent className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  const hasContent = lessons.length > 0 || sessions.length > 0;

  if (!hasContent) {
    return (
      <Card className="shadow-card">
        <CardContent className="py-12 text-center">
          <Calendar className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-muted-foreground text-sm">لا توجد دروس أو جلسات مجدولة</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Live Sessions */}
      {sessions.length > 0 && (
        <Card className="shadow-card border-primary/20">
          <CardContent className="p-0">
            <div className="p-4 border-b bg-primary/5">
              <h3 className="font-semibold text-foreground flex items-center gap-2 text-sm">
                <Video className="w-4 h-4 text-primary" />
                جلسات البث المباشر القادمة
              </h3>
            </div>
            <ScrollArea className="max-h-[250px]">
              <div className="divide-y">
                {sessions.map((s) => {
                  const isLive = s.status === "live";
                  return (
                    <div key={s.id} className="p-4 flex items-center gap-3 hover:bg-muted/30 transition-colors">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                        isLive ? "bg-red-100 animate-pulse" : "bg-primary/10"
                      }`}>
                        <Video className={`w-5 h-5 ${isLive ? "text-red-600" : "text-primary"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground text-sm truncate">{s.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(s.scheduled_at), "EEEE dd MMMM - HH:mm", { locale: ar })}
                        </p>
                        {s.description && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">{s.description}</p>
                        )}
                      </div>
                      <div className="shrink-0 flex flex-col items-end gap-1">
                        {isLive && (
                          <Badge variant="destructive" className="text-[10px] animate-pulse">
                            🔴 مباشر الآن
                          </Badge>
                        )}
                        {s.meeting_url && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs gap-1 h-7"
                            onClick={() => window.open(s.meeting_url, "_blank")}
                          >
                            <ExternalLink className="w-3 h-3" />
                            انضم
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Lessons */}
      {lessons.length > 0 && (
        <Card className="shadow-card">
          <CardContent className="p-0">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-foreground flex items-center gap-2 text-sm">
                <BookOpen className="w-4 h-4 text-primary" />
                الدروس ({lessons.length})
              </h3>
            </div>
            <ScrollArea className="max-h-[450px]">
              <div className="divide-y">
                {lessons.map((l) => (
                  <div
                    key={l.id}
                    className="p-4 flex items-start gap-3 hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => openLesson(l)}
                  >
                    {l.media_url ? (
                      <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0">
                        <img src={l.media_url} alt={l.title} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <BookOpen className="w-6 h-6 text-primary" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground text-sm">{l.title}</p>
                      {l.content && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{l.content}</p>
                      )}
                      {l.scheduled_at && (
                        <p className="text-[10px] text-muted-foreground/60 mt-1">
                          {format(new Date(l.scheduled_at), "EEEE dd MMMM yyyy", { locale: ar })}
                        </p>
                      )}
                    </div>
                    <Button size="sm" variant="ghost" className="text-xs gap-1 h-7 shrink-0">
                      <ExternalLink className="w-3 h-3" />
                      فتح
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Lesson Detail Dialog */}
      <Dialog open={!!selectedLesson} onOpenChange={(o) => { if (!o) { setSelectedLesson(null); setShowPlayer(false); setShowSlides(false); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
          {showPlayer && selectedLesson ? (
            <LessonPlayer
              lesson={selectedLesson}
              onClose={() => setShowPlayer(false)}
            />
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" />
                  {selectedLesson?.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {/* Listen Button */}
                {selectedLesson?.content && (
                  <Button
                    onClick={() => setShowPlayer(true)}
                    className="w-full gap-2 h-12 text-base"
                  >
                    <Headphones className="w-5 h-5" />
                    استمع للدرس 🎧
                  </Button>
                )}

                {/* View Slides Button */}
                {hasSlides && (
                  <Button
                    onClick={() => setShowSlides(true)}
                    variant="outline"
                    className="w-full gap-2 h-12 text-base border-primary/30 text-primary"
                  >
                    <Eye className="w-5 h-5" />
                    📚 عرض الشرح المبسط
                  </Button>
                )}

                {/* Cover Image */}
                {selectedLesson?.media_url && (
                  <img
                    src={selectedLesson.media_url}
                    alt={selectedLesson.title}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                )}

                {selectedLesson?.scheduled_at && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    {format(new Date(selectedLesson.scheduled_at), "EEEE dd MMMM yyyy", { locale: ar })}
                  </div>
                )}

                {/* Lesson Content */}
                {selectedLesson?.content ? (
                  <div className="bg-muted/50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-foreground mb-2">📖 محتوى الدرس</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                      {selectedLesson.content}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">لا يوجد محتوى لهذا الدرس</p>
                )}

                {/* Attached Files */}
                {loadingFiles ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : lessonFiles.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1">
                      <Paperclip className="w-4 h-4" />
                      المرفقات ({lessonFiles.length})
                    </h4>
                    <div className="space-y-2">
                      {lessonFiles.map((f: any) => {
                        const isImage = f.file_type === "image";
                        const isVideo = f.file_type === "video";
                        return (
                          <div key={f.id} className="border rounded-lg overflow-hidden">
                            {isImage && f.signed_url && (
                              <img src={f.signed_url} alt={f.file_name} className="w-full max-h-64 object-contain bg-muted/30" />
                            )}
                            {isVideo && f.signed_url && (
                              <video controls className="w-full max-h-64">
                                <source src={f.signed_url} />
                              </video>
                            )}
                            <div className="p-2 flex items-center gap-2 justify-between">
                              <div className="flex items-center gap-2 min-w-0">
                                {isImage ? <ImageIcon className="w-4 h-4 text-muted-foreground shrink-0" /> :
                                 isVideo ? <Video className="w-4 h-4 text-muted-foreground shrink-0" /> :
                                 <FileText className="w-4 h-4 text-muted-foreground shrink-0" />}
                                <span className="text-xs truncate">{f.file_name}</span>
                              </div>
                              {f.signed_url && (
                                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => window.open(f.signed_url, "_blank")}>
                                  فتح
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Slides Viewer */}
      {showSlides && hasSlides && selectedLesson && (
        <LessonSlidesViewer
          slides={lessonExplanation.slides as any[]}
          lessonTitle={selectedLesson.title}
          onClose={() => setShowSlides(false)}
        />
      )}
    </div>
  );
}